import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MapPin, Sparkles, Heart, X } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { optionLabel } from "@/lib/profileLabels";
import MatchModal from "@/components/MatchModal";
import OverflowMenu from "@/components/OverflowMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function Chips({ list, kind, lang }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((k) => (
        <span
          key={k}
          className="px-3 py-1 rounded-full text-xs border"
          style={{ borderColor: "hsl(var(--nvl-border))" }}
        >
          {optionLabel(lang, kind, k)}
        </span>
      ))}
    </div>
  );
}

function CompatBar({ score }) {
  return (
    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--muted))" }}>
      <div
        className="h-full transition-[width]"
        style={{ width: `${Math.round(score * 100)}%`, backgroundColor: "hsl(var(--nvl-accent))" }}
      />
    </div>
  );
}

function FactorRow({ label, factor, hint }) {
  if (!factor || factor.has_signal === false) return null;
  return (
    <div className="py-2.5 border-b" style={{ borderColor: "hsl(var(--nvl-border))" }}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm">{label}</span>
        <span className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {Math.round((factor.score || 0) * 100)}%
        </span>
      </div>
      <CompatBar score={factor.score || 0} />
      {hint ? <p className="mt-1.5 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{hint}</p> : null}
    </div>
  );
}

export default function DiscoverDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [matchPayload, setMatchPayload] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/discovery/profile/${userId}`);
        setData(data);
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const handleLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/interactions/like", { target_user_id: userId });
      if (res?.match) {
        setMatchPayload(res.match);
      } else {
        toast.success(t("app.discovery.likeSent"));
        navigate("/app/discover");
      }
    } catch (e) {
      toast.error(extractError(e, t("app.discovery.actionError")));
    } finally {
      setBusy(false);
    }
  };

  const handlePass = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/interactions/pass", { target_user_id: userId });
      toast(t("app.discovery.passDone"));
      navigate("/app/discover");
    } catch (e) {
      toast.error(extractError(e, t("app.discovery.actionError")));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="discover-detail-loading">
        <div className="nvl-card p-8 animate-pulse text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("common.loading")}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="discover-detail-error">
        <button onClick={() => navigate(-1)} className="text-xs underline mb-4 flex items-center">
          <ArrowLeft size={14} className="mr-1" /> {t("app.discovery.back")}
        </button>
        <div className="nvl-card p-8 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const p = data.profile || {};
  const compat = data.compatibility || {};
  const factors = compat.factors || {};
  const photos = p.photos || [];
  const mainPhoto = photos.find((x) => x.is_main) || photos[0];
  const mainUrl = photoUrl(mainPhoto?.url);

  const categoryLabel = compat.category ? t(`app.discovery.category.${compat.category}`) : "";

  const goalsShared = factors.relationship_goals?.shared || [];
  const interestsShared = factors.interests?.shared || [];
  const valuesShared = factors.values?.shared || [];
  const topValuesShared = factors.values?.shared_top || [];
  const commShared = factors.communication?.shared || [];
  const lifestyleSharedKeys = factors.lifestyle?.shared_keys || [];

  return (
    <div className="max-w-4xl mx-auto" data-testid="discover-detail-page">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-xs underline flex items-center"
          data-testid="detail-back"
        >
          <ArrowLeft size={14} className="mr-1" /> {t("app.discovery.back")}
        </button>
        <OverflowMenu
          userId={p.user_id || userId}
          onBlocked={() => navigate("/app/discover")}
          testIdPrefix="detail-overflow"
        />
      </div>

      <div className="nvl-card overflow-hidden">
        <div
          className="relative aspect-[4/5] md:aspect-[16/9] w-full flex items-end p-8"
          style={{
            backgroundImage: mainUrl ? `url(${mainUrl})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
            background: mainUrl ? undefined : "linear-gradient(135deg, hsl(6 43% 46% / 0.25), hsl(223 66% 10% / 0.95))",
            color: "hsl(var(--nvl-bg))",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: mainUrl ? "linear-gradient(180deg, transparent 40%, rgba(10,17,40,0.78))" : undefined }}
          />
          <div className="relative w-full flex flex-wrap items-end justify-between gap-4">
            <div>
              {p.verification_status === "verified" ? (
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest mb-3"
                  style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
                >
                  <ShieldCheck size={12} /> {t("app.discovery.verified")}
                </span>
              ) : null}
              <h1 className="font-display text-4xl md:text-5xl leading-tight">
                {p.display_name}{p.age ? <span className="opacity-80">, {p.age}</span> : null}
              </h1>
              {p.city ? (
                <p className="mt-2 text-sm flex items-center gap-2 opacity-90">
                  <MapPin size={14} /> {p.city}{p.country ? `, ${p.country}` : ""}
                  {typeof p.distance_km === "number" ? ` · ${Math.round(p.distance_km)} ${t("app.discovery.km")}` : ""}
                </p>
              ) : null}
            </div>
            <div className="text-right">
              {compat.confidence === "low" ? (
                <p className="text-xs italic opacity-90" data-testid="detail-compat-low">
                  {t("app.discovery.confidenceLearning")}
                </p>
              ) : (
                <>
                  <div className="font-display text-4xl md:text-5xl leading-none" data-testid="detail-compat-score">
                    {compat.score}%
                  </div>
                  <div className="text-[11px] uppercase tracking-widest opacity-90 mt-1">
                    {categoryLabel}
                  </div>
                  {compat.confidence === "medium" ? (
                    <p className="text-[10px] italic opacity-80 mt-1">{t("app.discovery.confidenceLimited")}</p>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-8">
          {compat.shared_perspective ? (
            <div
              className="nvl-card p-4 flex items-start gap-3"
              style={{ borderColor: "hsl(var(--nvl-accent))", backgroundColor: "hsl(var(--nvl-accent-soft, var(--muted)))" }}
              data-testid="detail-shared-perspective"
            >
              <Sparkles size={16} style={{ color: "hsl(var(--nvl-accent))" }} className="mt-0.5" />
              <p className="text-sm">{t("app.discovery.sharedPerspective")}</p>
            </div>
          ) : null}

          {/* Why you're compatible */}
          <div>
            <h2 className="font-display text-2xl md:text-3xl mb-4">{t("app.discovery.whyCompatible")}</h2>

            {compat.confidence === "low" ? (
              <p className="text-sm italic" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {t("app.discovery.insufficientCompatData")}
              </p>
            ) : (
              <div className="space-y-5">
                {interestsShared.length ? (
                  <div>
                    <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t("app.discovery.sharedInterests")}
                    </p>
                    <Chips list={interestsShared} kind="interests" lang={lang} />
                  </div>
                ) : null}
                {topValuesShared.length ? (
                  <div>
                    <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t("app.discovery.sharedTopValues")}
                    </p>
                    <Chips list={topValuesShared} kind="values" lang={lang} />
                  </div>
                ) : valuesShared.length ? (
                  <div>
                    <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t("app.discovery.sharedValues")}
                    </p>
                    <Chips list={valuesShared} kind="values" lang={lang} />
                  </div>
                ) : null}
                {goalsShared.length ? (
                  <div>
                    <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t("app.discovery.sharedGoals")}
                    </p>
                    <Chips list={goalsShared} kind="relationship_goals" lang={lang} />
                  </div>
                ) : null}
                {commShared.length ? (
                  <div>
                    <p className="text-xs mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t("app.discovery.sharedCommunication")}
                    </p>
                    <Chips list={commShared} kind="communication_style" lang={lang} />
                  </div>
                ) : null}

                <div className="pt-4 border-t" style={{ borderColor: "hsl(var(--nvl-border))" }}>
                  <p className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                    {t("app.discovery.compatibleLong")}
                  </p>
                  <FactorRow label={t("app.discovery.filters.interests")} factor={factors.interests} />
                  <FactorRow label={t("app.discovery.filters.values")} factor={factors.values} />
                  <FactorRow label={t("app.discovery.filters.relationshipGoals")} factor={factors.relationship_goals} />
                  <FactorRow
                    label={t("app.discovery.filters.lifestyle")}
                    factor={factors.lifestyle}
                    hint={lifestyleSharedKeys.length ? t("app.discovery.similarLifestyle") : null}
                  />
                  <FactorRow label={t("app.discovery.filters.family")} factor={factors.family} />
                  <FactorRow label={t("app.discovery.filters.religion")} factor={factors.religion} />
                  <FactorRow label={t("app.discovery.sharedCommunication")} factor={factors.communication} />
                  <FactorRow
                    label={t("app.discovery.filters.distance")}
                    factor={factors.geography}
                    hint={typeof factors.geography?.distance_km === "number" ? `${Math.round(factors.geography.distance_km)} ${t("app.discovery.km")}` : null}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Bio + gallery + everything else the profile chose to share */}
          {p.bio ? (
            <div>
              <p className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-accent))" }}>
                {lang === "it" ? "Su di lui/lei" : "About"}
              </p>
              <p className="text-sm md:text-base" data-testid="detail-bio">{p.bio}</p>
            </div>
          ) : null}

          {photos.length > 1 ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {lang === "it" ? "Foto" : "Photos"}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {photos.filter((p) => !p.is_main).slice(0, 6).map((ph) => {
                  const u = photoUrl(ph.url);
                  return <img key={ph.id || ph.url} src={u} alt="" className="w-full aspect-square object-cover rounded-md" />;
                })}
              </div>
            </div>
          ) : null}

          {p.top_values?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {lang === "it" ? "Valori principali" : "Top values"}
              </p>
              <Chips list={p.top_values} kind="values" lang={lang} />
            </div>
          ) : null}
          {p.interests?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.filters.interests")}
              </p>
              <Chips list={p.interests} kind="interests" lang={lang} />
            </div>
          ) : null}
          {p.relationship_goals?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.filters.relationshipGoals")}
              </p>
              <Chips list={p.relationship_goals} kind="relationship_goals" lang={lang} />
            </div>
          ) : null}
          {p.communication_style?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {lang === "it" ? "Comunicazione" : "Communication"}
              </p>
              <Chips list={p.communication_style} kind="communication_style" lang={lang} />
            </div>
          ) : null}
          {p.lifestyle && Object.values(p.lifestyle).some(Boolean) ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.filters.lifestyle")}
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6 text-sm">
                {p.lifestyle.lifestyle_type ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Vita":"Lifestyle"}: </span>{optionLabel(lang,"lifestyle_type",p.lifestyle.lifestyle_type)}</div> : null}
                {p.lifestyle.smoking ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{t("app.discovery.filters.smoking")}: </span>{optionLabel(lang,"smoking",p.lifestyle.smoking)}</div> : null}
                {p.lifestyle.alcohol ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{t("app.discovery.filters.alcohol")}: </span>{optionLabel(lang,"alcohol",p.lifestyle.alcohol)}</div> : null}
                {p.lifestyle.pets ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{t("app.discovery.filters.pets")}: </span>{optionLabel(lang,"pets",p.lifestyle.pets)}</div> : null}
                {p.lifestyle.diet ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{t("app.discovery.filters.diet")}: </span>{optionLabel(lang,"diet",p.lifestyle.diet)}</div> : null}
              </div>
            </div>
          ) : null}
          {p.family && Object.values(p.family).some(Boolean) ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.filters.family")}
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6 text-sm">
                {p.family.relationship_status ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Stato":"Status"}: </span>{optionLabel(lang,"relationship_status",p.family.relationship_status)}</div> : null}
                {p.family.children ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{t("app.discovery.filters.children")}: </span>{optionLabel(lang,"children",p.family.children)}</div> : null}
                {p.family.wants_children ? <div className="py-1"><span style={{color:"hsl(var(--nvl-ink-soft))"}}>{t("app.discovery.filters.wantsChildren")}: </span>{optionLabel(lang,"wants_children",p.family.wants_children)}</div> : null}
              </div>
            </div>
          ) : null}
          {p.religion?.religion ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{t("app.discovery.filters.religion")}</p>
              <p className="text-sm">{optionLabel(lang, "religion", p.religion.religion)}</p>
            </div>
          ) : null}
          {p.community?.covid_perspective ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>
                {t("app.discovery.filters.community")}
              </p>
              <p className="text-sm">{optionLabel(lang, "covid_perspective", p.community.covid_perspective)}</p>
              {p.community.covid_motivations?.length ? (
                <div className="mt-2"><Chips list={p.community.covid_motivations} kind="covid_motivations" lang={lang} /></div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="mt-6 sticky bottom-4 z-30 flex gap-3 max-w-md mx-auto">
        <button
          type="button"
          onClick={handlePass}
          disabled={busy}
          data-testid="detail-pass"
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md py-3 text-sm border transition-colors hover:bg-[hsl(var(--muted))] disabled:opacity-50"
          style={{
            borderColor: "hsl(var(--nvl-border))",
            backgroundColor: "hsl(var(--nvl-bg))",
            boxShadow: "0 10px 30px -12px rgba(10,17,40,0.25)",
          }}
        >
          <X size={16} />
          {t("app.discovery.pass")}
        </button>
        <button
          type="button"
          onClick={handleLike}
          disabled={busy}
          data-testid="detail-like"
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md py-3 text-sm text-[hsl(var(--nvl-bg))] transition-colors disabled:opacity-50"
          style={{
            backgroundColor: "hsl(var(--nvl-accent))",
            boxShadow: "0 10px 30px -12px rgba(214,86,74,0.5)",
          }}
        >
          <Heart size={16} />
          {t("app.discovery.like")}
        </button>
      </div>

      <MatchModal
        open={!!matchPayload}
        match={matchPayload}
        onClose={() => {
          setMatchPayload(null);
          navigate("/app/discover");
        }}
      />
    </div>
  );
}
